import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import _ from "lodash";
import TablesStateContext from "../contexts/TablesStateContext";

type InitialFiltersBuilder<T extends Record<string, unknown>> = (
  savedFilters: T | undefined
) => T;

type UseTableFiltersCursorResult<T extends Record<string, unknown>> = {
  queryFilters: T;
  setQueryFilters: Dispatch<SetStateAction<T>>;
  cursor: string | undefined;
  setCursor: (cursor: string | undefined) => void;
};

export function useTableFiltersCursor<T extends Record<string, unknown>>(
  tableKey: string,
  buildInitialFilters?: InitialFiltersBuilder<T>
): UseTableFiltersCursorResult<T> {
  const { getTableState, setCursor: setTableCursor, setFilters } =
    useContext(TablesStateContext);
  const tableState = getTableState(tableKey);

  const [queryFilters, setQueryFilters] = useState<T>(() => {
    const savedFilters = tableState.filters as T | undefined;
    return buildInitialFilters?.(savedFilters) ?? savedFilters ?? ({} as T);
  });

  useEffect(() => {
    setFilters(tableKey, queryFilters);
  }, [queryFilters, setFilters, tableKey]);

  const filtersInSync = _.isEqual(tableState.filters ?? {}, queryFilters);
  const cursor = filtersInSync ? tableState.cursor : undefined;
  const setCursor = (nextCursor: string | undefined) => {
    setTableCursor(tableKey, nextCursor);
  };

  return { queryFilters, setQueryFilters, cursor, setCursor };
}
